import React, { useState } from 'react';
import { Alert, Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../../redux/store';
import { loginUser } from '../../redux/features/auth/authSlice';
import { portalHome } from '../../routes/appRoutePermissions';
import AuthPageFrame from './AuthPageFrame';
import RareButton from './buttons/RareButton';
import { authFieldSx, authInputSx, authLabelProps, handlePasswordVisibilityShortcut } from './fields/AuthFields';

const SignIn = () => {
  const [isSubmit, setSubmit] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authData = useSelector((state: RootState) => state.auth);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    const data = new FormData(event.currentTarget);
    setLoading(true);
    setAuthFailed(false);
    try {
      unwrapResult(await dispatch(loginUser({
        username: String(data.get('email') || ''),
        password: String(data.get('password') || ''),
      })));
      setSubmit(true);
    } catch {
      setAuthFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (isSubmit) return <Navigate to={portalHome(authData)} />;

  return (
    <AuthPageFrame title="Sign In" loading={loading}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {authFailed && <Alert severity="error">Login failed, please check email and password</Alert>}
        <TextField
          required
          fullWidth
          id="email"
          name="email"
          type="email"
          label="Email Address"
          autoComplete="email"
          autoFocus
          sx={authFieldSx}
          InputProps={{ sx: authInputSx }}
          InputLabelProps={authLabelProps}
        />
        <TextField
          required
          fullWidth
          id="password"
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          onKeyDown={(event) => handlePasswordVisibilityShortcut(event, () => setShowPassword(value => !value))}
          inputProps={{ 'aria-keyshortcuts': 'Alt+Shift+V' }}
          autoComplete="current-password"
          sx={authFieldSx}
          InputLabelProps={authLabelProps}
          InputProps={{
            sx: authInputSx,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={`${showPassword ? 'Hide' : 'Show'} password (Alt+Shift+V)`}
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #e1e8e6', display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <RareButton variant="outlined" onClick={() => navigate('/forgot-password')} sx={{ mr: { sm: 'auto' }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            Forgot password?
          </RareButton>
          <RareButton variant="contained" type="submit" disabled={loading} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            Sign In
          </RareButton>
        </Box>
      </Box>
    </AuthPageFrame>
  );
};

export default SignIn;
